import type { ChangeEvent, FunctionComponent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";

import Sentry from "@similarweb/sentry-logger";
import { DotsLoader } from "@similarweb/ui-components";

import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient, keywordGroupsTransforms, useAllKeywordGroups } from "app/services/keyword-groups";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import {
    getAutocompleteKeywordsForQuery,
    getAutocompleteRecentKeywordSearches,
} from "pro-features/components/AutocompleteKeywords/helpers/KeywordAutocompleteDataHandler";
import { AutocompleteStyled } from "pro-features/components/AutocompleteKeywords/styles/AutocompleteKeywordStyles";
import type {
    IAutocompleteKeywordGroupsProps,
    IAutocompleteKeywordGroup,
    ISearchedKeyword,
    IKeywordAutocompleteResults,
    IAutocompleteKeyword,
    AvailableTabName,
    IAutocompleteKeywordGroupsDeleteContext,
} from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import { getName } from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import { useAutoCompleteClick } from "pro-features/components/AutocompleteWebCategories/utils";
import { useAccountUsers } from "app/custom-hooks/useAccountUsers";
import { i18nFilter } from "app/filters/i18nFilter";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { useKeywordsGroupEditorController } from "app/pages/keyword-analysis/useKeywordsGroupEditorController";
import {
    KeywordsSharingModal,
    transformAccountUsers,
} from "app/pages/keyword-research/keyword-analysis/pages/monitor-lists/KeywordsSharingModal";
import { KeywordGroupsShareModal } from "app/pages/workspace/marketing/pages/KeywordGroupShareModal";
import { DefaultFetchService } from "app/services/fetchService";
import { SharingService } from "app/sharing/SharingService";

import { RenderAutocompleteContent } from "./helpers/renderAutocompleteContent";
import { allTrackers } from "app/services/track/track";

enum AutocompleteTab {
    KEYWORDS = 0,
    KEYWORD_GROUPS = 1,
}

type DropdownItem = IAutocompleteKeyword | IAutocompleteKeywordGroup;

interface Content {
    [AutocompleteTab.KEYWORDS]: IAutocompleteKeyword[];
    [AutocompleteTab.KEYWORD_GROUPS]: IAutocompleteKeywordGroup[];
}

const selectedTabNameToIndex = (tab: AvailableTabName) => {
    switch (tab) {
        case "keywordGroups":
            return AutocompleteTab.KEYWORD_GROUPS;
        case "keywords":
        default:
            return AutocompleteTab.KEYWORDS;
    }
};

export const AutocompleteKeywordGroups: FunctionComponent<IAutocompleteKeywordGroupsProps> = ({
    selectedValue = "",
    onClick,
    onAutoCreateKeywordGroupClick,
    className,
    autocompleteProps = {},
    forceResultsView = false,
    defaultSelectedTab = "keywords",
    onGroupShareModalOpened,
    onGroupShareModalClosed,
    onGroupDelete,
    onClearSearch,
    showKeywords = true,
    onValueChange = () => {},
    onGroupEditorModalOpened = () => {},
    onGroupEditorModalClosed = () => {},
    overrideEnterFunc,
    refocusOnItemSelect = false,
    clearTextValueOmItemSelect = false,
    showEditButton = true,
    showShareButton = true,
    includeComparisons = false,
    showAutoCreateKeywordGroupButton = true,
    keywordsToFilterFromAutocomplete,
    keywordsGroupsToFilterFromAutocomplete,
    truncateResultsOnBodyClick = true,
    showGoToMonitorKeywordListButton = false,
    onBeforeDeleteKeywordGroup,
    handleSave,
    controller,
    tabTitles = {
        keywordsTabTitle: "autocompleteKeywordsGroup.keywordsTab.Title",
        keywordGroupsTabTitle: "autocompleteKeywordsGroup.keywordsGroupTab.Title",
    },
}: IAutocompleteKeywordGroupsProps) => {
    const services = useMemo(() => {
        return {
            translate: i18nFilter(),
            sharingService: SharingService,
            fetchService: DefaultFetchService.getInstance(),
        };
    }, []);

    const [selectedIndex, setSelectedIndex] = useState(selectedTabNameToIndex(defaultSelectedTab));

    const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
    const [isInitialQuery, setIsInitialQuery] = useState(true);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [groupToShare, setGroupToShare] = useState<IUserKeywordGroup | null>(null);
    const internalEditorController = useKeywordsGroupEditorController();
    const editorController = controller ?? internalEditorController;

    const selectedValueFormatted = useMemo(() => getName(selectedValue), [selectedValue]);
    const [keywordQuery, setKeywordQuery] = useState<string>(selectedValueFormatted);
    const [isDirtyState, setIsDirtyState] = useState<boolean>(false);
    const [keywordsResults, setKeywordsResults] = useState<Content>({
        [AutocompleteTab.KEYWORDS]: [],
        [AutocompleteTab.KEYWORD_GROUPS]: [],
    });

    const autocompleteRef = useRef<FixmeType>(null);
    useAutoCompleteClick(autocompleteRef, truncateResultsOnBodyClick);
    const { data: accountUsers = [] } = useAccountUsers();
    const { userGroups, sharedGroups } = useAllKeywordGroups();

    useEffect(() => {
        setKeywordQuery(selectedValueFormatted);
    }, [selectedValueFormatted]);

    const onTabSelected = (index: number) => {
        setSelectedIndex(index);
    };

    const renderItemsClick =
        (item: DropdownItem | string, isKwGroup?: boolean) => async (e: ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            if (clearTextValueOmItemSelect) {
                autocompleteRef.current.textFieldRef.clearValue();
            }
            if (!refocusOnItemSelect) {
                autocompleteRef?.current?.truncateResults(true);
            }
            if (item instanceof Array && includeComparisons) {
                const keywords: (IUserKeywordGroup | { name: string })[] = await Promise.all(
                    item.map(
                        async (keyword) => (await keywordGroupsQueryClient.getGroupById(keyword)) ?? { name: keyword },
                    ),
                );
                onClick(keywords, e);
            } else if (isKwGroup && typeof item === "string") {
                onClick({ Id: item }, e);
            } else {
                onClick(item, e);
            }
        };

    /**
     * Resolves the query text that should be sent for the autocomplete search
     */
    const resolveQueryInput = (query: string) => {
        const hasQuery = typeof query === "string" && query !== "";

        if (hasQuery) {
            return query;
        }

        // In case no query is present, but the component user wants to show
        // results immediately, we should use the initial selected value as the query input.
        // this logic is used to display immediate search results upon opening the autocomplete
        // search in the new keywords query bar.
        if (isInitialQuery && forceResultsView) {
            setIsInitialQuery(false);
            return selectedValue;
        }

        return null;
    };

    const getKeywords = async (query: string, hasQuery: boolean): Promise<IAutocompleteKeyword[]> => {
        const result = hasQuery
            ? (await getAutocompleteKeywordsForQuery(query, services.fetchService)).map(
                  (site) =>
                      ({
                          name: site.name,
                          comparedItems: [],
                      }) as IAutocompleteKeyword,
              )
            : getAutocompleteRecentKeywordSearches(includeComparisons);
        return keywordsToFilterFromAutocomplete
            ? result.filter(({ name }) => !keywordsToFilterFromAutocomplete.includes(name))
            : result;
    };

    const getKeywordGroups = async (query: string, hasQuery: boolean): Promise<IAutocompleteKeywordGroup[]> => {
        const keywordGroups = (await keywordGroupsQueryClient.getUserGroups()).map((group) => ({
            ...group,
            SharedWithMe: false,
        }));

        const result = hasQuery
            ? keywordGroups.filter(({ Name }) => Name.toLowerCase().includes(query.toLowerCase()))
            : keywordGroups;
        return keywordsGroupsToFilterFromAutocomplete
            ? result.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
            : result;
    };

    const getSharedKeywordGroups = async (query: string, hasQuery: boolean): Promise<IAutocompleteKeywordGroup[]> => {
        type IExtendedKeywordGroupUserData = IUserKeywordGroup & { SharedWithMe: boolean };
        const sharedKwGroups: IExtendedKeywordGroupUserData[] = (await keywordGroupsQueryClient.getSharedGroups()).map(
            (group: IUserKeywordGroup) => ({ ...group, SharedWithMe: true }),
        );

        const result = hasQuery
            ? sharedKwGroups.filter(({ Name }) => Name.toLowerCase().includes(query.toLowerCase()))
            : sharedKwGroups;
        return keywordsGroupsToFilterFromAutocomplete
            ? result.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
            : result;
    };

    const getAutocompleteData = async (queryInput: string): Promise<IKeywordAutocompleteResults> => {
        onValueChange(queryInput);
        setIsAutocompleteLoading(true);

        try {
            const query = resolveQueryInput(queryInput) as string;
            let isNextStateDirty = isDirtyState;

            // If the query is the same as the initial query, we should not perform a new search
            // unless we are in a dirty state or the query is empty.
            if (query !== selectedValue || isDirtyState || !query?.length) {
                isNextStateDirty = true;
                setIsDirtyState(true);
                setKeywordQuery(query);
            }

            const searchText = isNextStateDirty ? query : keywordQuery;
            const hasQuery = typeof searchText === "string" && searchText !== "";
            if (!hasQuery && selectedValue) {
                if (onClearSearch) {
                    onClearSearch();
                }
            }

            let keywords = await getKeywords(searchText, hasQuery);
            let keywordGroups = await getKeywordGroups(searchText, hasQuery);
            let sharedKeywordGroups = await getSharedKeywordGroups(searchText, hasQuery);

            setKeywordsResults({
                [AutocompleteTab.KEYWORDS]: keywords,
                [AutocompleteTab.KEYWORD_GROUPS]: [...keywordGroups, ...sharedKeywordGroups],
            });

            // in case we are in clean state and there is no data in secondary tab
            if (!isNextStateDirty) {
                if (!keywords.length) {
                    keywords = await getKeywords("", false);
                } else if (!keywordGroups.length && !sharedKeywordGroups.length) {
                    keywordGroups = await getKeywordGroups("", false);
                    sharedKeywordGroups = await getSharedKeywordGroups("", false);
                }
            }

            // sim-32009: after the users type anything, if there are keyword results and no keyword group results,
            // move to the keywords tab
            if (hasQuery && keywords.length > 0 && keywordGroups.length === 0 && sharedKeywordGroups.length === 0) {
                setSelectedIndex(AutocompleteTab.KEYWORDS);
            }
            // sim-32009: switch to kwgroups if the user doesnt have recent searched and he has at least on group
            else if (keywords.length === 0 && (keywordGroups.length > 0 || sharedKeywordGroups.length > 0)) {
                setSelectedIndex(AutocompleteTab.KEYWORD_GROUPS);
            }

            return {
                hasQuery: hasQuery,
                keywords: keywords,
                keywordGroups: keywordGroups,
                sharedKeywordGroups: sharedKeywordGroups,
            };
        } catch (error) {
            Sentry.errorTracker({
                level: "error",
                message: "Failed to fetch autocomplete data",
                e: error,
                params: {
                    extra: {
                        queryInput,
                        includeComparisons,
                        selectedValue,
                        isDirtyState,
                    },
                },
            });
            return {
                hasQuery: false,
                keywords: [],
                keywordGroups: [],
                sharedKeywordGroups: [],
            };
        } finally {
            setIsAutocompleteLoading(false);
        }
    };

    const getCurrentItemsInTab = (data: IKeywordAutocompleteResults) => {
        switch (selectedIndex) {
            case AutocompleteTab.KEYWORDS:
                return data.keywords;
            case AutocompleteTab.KEYWORD_GROUPS:
                return [...data.keywordGroups, ...data.sharedKeywordGroups];
        }
    };

    const handleGroupEditClick = (item: IUserKeywordGroup) => {
        editorController.openSession({ keywordsGroup: item });
        allTrackers.dispatchTrackEvent("Button", "click", "Edit Keyword Group/Edit on Dropdown");
        onGroupEditorModalOpened?.();
    };

    const handleGroupShareClick = (item: IUserKeywordGroup) => {
        const groupToShare = keywordGroupsTransforms.adaptGroupDataForShareModal(item, [
            ...userGroups,
            ...sharedGroups,
        ]);
        setGroupToShare(groupToShare);
        setIsShareModalOpen(true);

        if (onGroupShareModalOpened) {
            onGroupShareModalOpened();
        }
    };

    const handleGroupShareClose = () => {
        setIsShareModalOpen(false);
        setGroupToShare(null);

        if (onGroupShareModalClosed) {
            onGroupShareModalClosed();
        }
    };

    const handleEditorModalClose = () => {
        editorController.hide();
        onGroupEditorModalClosed();
    };

    const handleBeforeDeleteKeywordGroup = (group: IUserKeywordGroup) => {
        if (!onBeforeDeleteKeywordGroup) {
            return;
        }

        const context: IAutocompleteKeywordGroupsDeleteContext = {
            controller: editorController,
            onDeleted: onGroupDelete,
        };

        onBeforeDeleteKeywordGroup(group, context);
    };

    const handleAutoCreateKeywordGroupClick = (item: ISearchedKeyword) => {
        if (onAutoCreateKeywordGroupClick) {
            // @ts-ignore FIXME: strict migration - Argument of type 'null' is not assignable to parameter of type 'ChangeEvent<HTMLInputElement>' — no DOM event exists for an auto-create action; the handler ignores the event argument
            onAutoCreateKeywordGroupClick(item, null);
            return;
        }
        editorController.openSession({ seedKeyword: item.name });
        allTrackers.dispatchTrackEvent("Button", "click", "Auto Create KW Lists CTA clicked/Dropdown");
    };

    const overrideEnter = (event: ChangeEvent<HTMLInputElement>, selectedItem: FixmeType) => {
        const text = event.target.value;
        if (selectedItem != null) {
            if (overrideEnterFunc) {
                overrideEnterFunc(selectedItem, selectedIndex);
            } else {
                renderItemsClick(selectedItem)(event);
            }
        } else if (text) {
            autocompleteRef?.current?.truncateResults(true);
            //on case entered on group we take the first one in the list
            const isKwGroup = selectedIndex === AutocompleteTab.KEYWORD_GROUPS;
            // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type because expression of type '0' can't be used to index type 'Promise<IAutocompleteKeywordGroup[]>' — pre-existing missing await; preserving legacy runtime behavior
            const value = isKwGroup ? getKeywordGroups(text, text !== "")[0].Id : text;
            if (overrideEnterFunc) {
                overrideEnterFunc(value, selectedIndex);
            } else {
                renderItemsClick(value, isKwGroup)(event);
            }
        } else {
            // in case the user pressed enter without selecting anything, select first item
            const firstItem = keywordsResults[selectedIndex][0];
            if (overrideEnterFunc) {
                overrideEnterFunc(firstItem, selectedIndex);
            } else {
                renderItemsClick(firstItem)(event);
            }
        }
        if (clearTextValueOmItemSelect) {
            autocompleteRef.current.textFieldRef.clearValue();
        }
    };

    const renderAutocompleteContent = (data: { listItems: IKeywordAutocompleteResults; selectedItemId: number }) => (
        <RenderAutocompleteContent
            data={data}
            selectedIndex={selectedIndex}
            onTabSelected={onTabSelected}
            // @ts-ignore FIXME: strict migration - Type '(item: DropdownItem | string, ...) => ...' is not assignable to prop type '(item: unknown, ...) => ...' — contravariant fn-param mismatch; the prop uses `unknown` to also accept string[] items forwarded from KeywordsSection
            renderItemsClick={renderItemsClick}
            handleGroupEditClick={handleGroupEditClick}
            handleGroupShareClick={handleGroupShareClick}
            handleAutoCreateKeywordGroupClick={handleAutoCreateKeywordGroupClick}
            showKeywords={showKeywords}
            isAutocompleteLoading={isAutocompleteLoading}
            hasQuery={data.listItems.hasQuery}
            tabTitles={tabTitles}
            showEditButton={showEditButton}
            showShareButton={showShareButton}
            showAutoCreateKeywordGroupButton={showAutoCreateKeywordGroupButton}
        />
    );

    const onAutocompleteBlur = () => {
        setIsDirtyState(false);
    };

    // Determine if we should show filtered results or not
    // we should show them if we are on the initial tab
    // or if we are not on the initial tab but still have results
    const shouldDisplayResultsForSelectedValue = (tabType: AutocompleteTab) => {
        return (
            selectedIndex === tabType &&
            (selectedTabNameToIndex(defaultSelectedTab) === selectedIndex || keywordsResults[tabType].length > 0)
        );
    };

    const selectedValueForCurrentTab =
        isDirtyState ||
        shouldDisplayResultsForSelectedValue(AutocompleteTab.KEYWORDS) ||
        shouldDisplayResultsForSelectedValue(AutocompleteTab.KEYWORD_GROUPS)
            ? keywordQuery
            : "";
    const isKeywordListSmartSharingFeatureDisabled =
        swSettings.components.KeywordAnalysis.resources.KeywordListSmartSharingFeature === EAvailabilityMode.HIDDEN;

    return (
        <>
            {isKeywordListSmartSharingFeatureDisabled && isShareModalOpen ? (
                <KeywordGroupsShareModal
                    keywordGroup={groupToShare}
                    isOpen={isShareModalOpen}
                    users={accountUsers}
                    onCloseClick={handleGroupShareClose}
                    onFinish={handleGroupShareClose}
                />
            ) : !isKeywordListSmartSharingFeatureDisabled && groupToShare ? (
                <KeywordsSharingModal
                    isOpen={isShareModalOpen}
                    keywordGroup={groupToShare}
                    users={transformAccountUsers(accountUsers)}
                    onCloseClick={handleGroupShareClose}
                    onSubmit={handleGroupShareClose}
                />
            ) : null}
            <AutocompleteStyled
                className={classNames("AutocompleteWithTabs", className)}
                getListItems={getAutocompleteData}
                getSelectableListItems={getCurrentItemsInTab}
                renderItems={renderAutocompleteContent}
                ref={autocompleteRef}
                loadingComponent={<DotsLoader />}
                isLoading={isAutocompleteLoading}
                debounce={250}
                floating
                preventTruncateUnlessForced
                shouldUpdateSelectedValue
                onBlur={onAutocompleteBlur}
                selectedValue={selectedValueForCurrentTab}
                overrideEnterFunc={overrideEnter}
                {...autocompleteProps}
            />
            <KeywordsGroupEditorModal
                onClose={handleEditorModalClose}
                onDelete={onGroupDelete}
                onBeforeDelete={onBeforeDeleteKeywordGroup ? handleBeforeDeleteKeywordGroup : undefined}
                onSave={handleSave}
                showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
                controller={editorController}
            />
        </>
    );
};
